import React, { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { useQuery } from 'react-query';
import { Alert } from '@material-ui/lab';
import { Box, makeStyles, Typography, Button, TextField, IconButton, SvgIconTypeMap } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import JsonDisplayModal from '../common/JsonDisplay/JsonDisplayModal';
import JsonDisplay from '../common/JsonDisplay/JsonDisplay';
import { getRequest } from '../../utils/httpClient';

const useStyles = makeStyles((theme) => ({
	modalDataCardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: '1vh'
	},
	centralBox: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		maxHeight: '80vh',
		minHeight: '80vh'
	}
}));

export const FarsideViewer: React.FC<AlignmentViewerProps> = (props) => {
	const { id, sport, open, setOpen, icon } = props;
	const classes = useStyles();
	const [progressionIdFilter, setProgressionIdFilter] = useState<string | null>(null);

	const {
		isLoading,
		data: progressionsData,
		isError,
		refetch,
		remove
	} = useQuery(['alignment', id], () => getRequest('sports-data', `/getProgressions?gameId=${id}&sport=${sport}`), { enabled: false });

	useEffect(() => {
		if (open) refetch();
		else remove();
	}, [open, refetch, remove]);

	let filteredAlignment = progressionsData;
	if (progressionIdFilter && filteredAlignment) {
		filteredAlignment = filteredAlignment.filter((progression) => {
			return progression.id === progressionIdFilter;
		});
	}

	const debouncedClick = useCallback(
		_.debounce(
			() => {
				refetch();
			},
			2000,
			{ leading: true, trailing: false, maxWait: 2000 }
		),
		[]
	);

	return (
		<JsonDisplayModal title={'Farside Viewer'} open={open} setOpen={setOpen} icon={icon}>
			{isLoading && (
				<Box className={classes.centralBox}>
					<CircularProgress />
				</Box>
			)}

			{isError && (
				<Box className={classes.centralBox}>
					<Typography variant='subtitle2'>Error getting progressions</Typography>{' '}
				</Box>
			)}

			{progressionsData && (
				<>
					{sport.toLowerCase() === 'basketball' && (
						<Alert severity='info' style={{ marginBottom: '1.5vh' }}>
							Basketball progressions are delivered entirely by MAF and may differ from the Farside data shown
						</Alert>
					)}
					<Box className={classes.modalDataCardHeader}>
						<TextField
							size='small'
							id='id'
							label='Search Progression ID'
							name='id'
							value={progressionIdFilter ?? ''}
							onChange={(e): void => setProgressionIdFilter(e.target.value)}
							variant='outlined'
							InputProps={{
								endAdornment: progressionIdFilter ? (
									<IconButton size='small' onClick={(): void => setProgressionIdFilter(null)}>
										<ClearIcon />
									</IconButton>
								) : undefined
							}}
						/>

						<Button color='primary' onClick={(): void => debouncedClick()}>
							<RefreshIcon />
						</Button>
					</Box>
					<JsonDisplay
						data={filteredAlignment}
						groupArraysAfterLength={500}
						shouldCollapse={(field): boolean => {
							return field.namespace.length > 2;
						}}
					/>
				</>
			)}
		</JsonDisplayModal>
	);
};

type AlignmentViewerProps = { id: string; sport: string; open: boolean; setOpen: Dispatch<SetStateAction<boolean>>; icon: SvgIconTypeMap };

export default FarsideViewer;
